import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, AlertTriangle, Truck, PackageCheck } from "lucide-react";
import {
  adminOrdersQuery,
  adminUpdateOrderStatus,
  adminGenerateShippingLabel,
} from "@/lib/admin/admin.functions";
import { adminRefundOrder } from "@/lib/admin/refunds.functions";
import { SHIPPING_FALLBACK_ID } from "@/lib/mercadopago.functions";
import { formatBRL } from "@/lib/format";

export const Route = createFileRoute("/admin/pedidos")({
  component: AdminOrdersPage,
});

const STATUSES = [
  "pending",
  "paid",
  "processing",
  "shipped",
  "delivered",
  "cancelled",
  "refunded",
] as const;

const STATUS_LABEL: Record<string, string> = {
  pending: "Aguardando pagamento",
  paid: "Pagamento confirmado",
  processing: "Em processamento",
  shipped: "Enviado",
  delivered: "Entregue",
  cancelled: "Cancelado",
  refunded: "Reembolsado",
};

function AdminOrdersPage() {
  const queryClient = useQueryClient();
  const { data: orders, isLoading } = useQuery(adminOrdersQuery());
  const [refundingId, setRefundingId] = useState<string | null>(null);
  const [refundError, setRefundError] = useState<{ id: string; message: string } | null>(null);
  const [generatingLabelId, setGeneratingLabelId] = useState<string | null>(null);
  const [labelError, setLabelError] = useState<{ id: string; message: string } | null>(null);

  const handleGenerateLabel = async (id: string) => {
    setGeneratingLabelId(id);
    setLabelError(null);
    try {
      await adminGenerateShippingLabel({ data: { id } });
    } catch (error) {
      setLabelError({
        id,
        message: error instanceof Error ? error.message : "Falha ao gerar a etiqueta.",
      });
    } finally {
      setGeneratingLabelId(null);
      queryClient.invalidateQueries({ queryKey: ["admin", "orders"] });
    }
  };

  const handleStatusChange = async (id: string, status: string) => {
    if (status === "refunded") {
      setRefundingId(id);
      setRefundError(null);
      try {
        await adminRefundOrder({ data: { id } });
      } catch (error) {
        setRefundError({
          id,
          message: error instanceof Error ? error.message : "Falha ao reembolsar o pedido.",
        });
      } finally {
        setRefundingId(null);
        queryClient.invalidateQueries({ queryKey: ["admin", "orders"] });
      }
      return;
    }
    await adminUpdateOrderStatus({ data: { id, status } });
    queryClient.invalidateQueries({ queryKey: ["admin", "orders"] });
  };

  return (
    <div>
      <h1 className="font-display text-2xl font-bold text-eg-dark mb-6">Pedidos</h1>

      {isLoading && (
        <div className="flex justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-eg-gold" />
        </div>
      )}

      <div className="overflow-x-auto rounded-lg border border-border bg-white">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-xs uppercase text-muted-foreground">
              <th className="px-4 py-3">Pedido</th>
              <th className="px-4 py-3">Cliente</th>
              <th className="px-4 py-3">Total</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Frete</th>
              <th className="px-4 py-3">Etiqueta</th>
              <th className="px-4 py-3">Tiny</th>
              <th className="px-4 py-3">Reembolso</th>
            </tr>
          </thead>
          <tbody>
            {orders?.map((order) => (
              <tr key={order.id} className="border-b border-border last:border-0">
                <td className="px-4 py-3 font-medium text-eg-dark">#{order.order_number}</td>
                <td className="px-4 py-3">
                  <p className="text-eg-dark">{order.customer_name}</p>
                  <p className="text-xs text-muted-foreground">{order.customer_email}</p>
                </td>
                <td className="px-4 py-3">{formatBRL(order.total)}</td>
                <td className="px-4 py-3">
                  <select
                    className="rounded-md border border-border px-2 py-1 text-xs"
                    value={order.status}
                    disabled={refundingId === order.id}
                    onChange={(e) => handleStatusChange(order.id, e.target.value)}
                  >
                    {STATUSES.map((s) => (
                      <option key={s} value={s}>
                        {STATUS_LABEL[s]}
                      </option>
                    ))}
                  </select>
                  {refundError?.id === order.id && (
                    <p className="mt-1 text-xs text-red-600">{refundError.message}</p>
                  )}
                </td>
                <td className="px-4 py-3">
                  {order.shipping_service_id === SHIPPING_FALLBACK_ID && (
                    <span
                      title="Frete não foi cobrado no pagamento — combine o valor com o cliente e cobre à parte."
                      className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-1 text-xs font-semibold text-amber-800"
                    >
                      <Truck className="h-3 w-3" />A combinar
                    </span>
                  )}
                </td>
                <td className="px-4 py-3">
                  {order.melhor_envio_tracking_code ? (
                    order.melhor_envio_tracking_url ? (
                      <a
                        href={order.melhor_envio_tracking_url}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-1 text-xs font-semibold text-green-800 hover:bg-green-200"
                      >
                        <PackageCheck className="h-3 w-3" />
                        {order.melhor_envio_tracking_code}
                      </a>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-1 text-xs font-semibold text-green-800">
                        <PackageCheck className="h-3 w-3" />
                        {order.melhor_envio_tracking_code}
                      </span>
                    )
                  ) : (
                    !["pending", "cancelled", "refunded"].includes(order.status) &&
                    order.shipping_service_id &&
                    order.shipping_service_id !== SHIPPING_FALLBACK_ID && (
                      <button
                        type="button"
                        disabled={generatingLabelId === order.id}
                        onClick={() => handleGenerateLabel(order.id)}
                        title={
                          order.melhor_envio_label_failed_at
                            ? `Última tentativa falhou em ${new Date(order.melhor_envio_label_failed_at).toLocaleString("pt-BR")} — clique para tentar de novo.`
                            : undefined
                        }
                        className="inline-flex items-center gap-1 rounded-md border border-border px-2 py-1 text-xs font-medium text-eg-dark hover:bg-muted disabled:opacity-60"
                      >
                        {generatingLabelId === order.id ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : order.melhor_envio_label_failed_at ? (
                          <AlertTriangle className="h-3 w-3 text-red-600" />
                        ) : null}
                        Gerar etiqueta
                      </button>
                    )
                  )}
                  {labelError?.id === order.id && (
                    <p className="mt-1 text-xs text-red-600">{labelError.message}</p>
                  )}
                </td>
                <td className="px-4 py-3">
                  {order.tiny_sync_failed_at && (
                    <span
                      title={`Falha ao sincronizar com o Tiny em ${new Date(order.tiny_sync_failed_at).toLocaleString("pt-BR")} — reconcilie manualmente.`}
                      className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-1 text-xs font-semibold text-red-700"
                    >
                      <AlertTriangle className="h-3 w-3" />
                      Falha de sync
                    </span>
                  )}
                </td>
                <td className="px-4 py-3">
                  {order.mp_refund_failed_at && (
                    <span
                      title={`Falha ao reembolsar no Mercado Pago em ${new Date(order.mp_refund_failed_at).toLocaleString("pt-BR")} — selecione "Reembolsado" novamente para tentar de novo.`}
                      className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-1 text-xs font-semibold text-red-700"
                    >
                      <AlertTriangle className="h-3 w-3" />
                      Falha de reembolso
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!isLoading && orders?.length === 0 && (
          <p className="p-6 text-center text-sm text-muted-foreground">Nenhum pedido ainda.</p>
        )}
      </div>
    </div>
  );
}
